import React from "react";

const RETRY_DELAY_MS = 1000;
// Per crash-bot reports of one-off dynamic import failures right after a
// deploy (see #web-logs) - 1 initial attempt + 5 retries.
const RETRY_ATTEMPTS = 6;

const FAILED_MODULE_URL_RE =
  /Failed to fetch dynamically imported module: (\S+)/;
// Vite's own preload helper throws this (see its bundled `preload-helper`
// chunk) when a chunk's CSS dependency fails to load via a <link> tag.
const FAILED_CSS_URL_RE = /Unable to preload CSS for (\S+)/;

// Every known cross-browser phrasing for "a lazy-loaded chunk failed to
// load" - Chrome/Vite's own two messages above, plus Firefox's shorter
// `'<mimetype>' is not a valid JavaScript MIME type.` (no URL in the
// message at all) for the same Cloudflare-serves-index.html-as-a-fallback
// scenario. Used to tell "the network/deploy hiccuped" apart from "the
// lazily-loaded module's own code threw" so the crash screen doesn't blame
// the user's connection for an actual bug, and vice versa.
const CHUNK_LOAD_ERROR_RE =
  /Failed to fetch dynamically imported module|Unable to preload CSS for|is not a valid JavaScript MIME type|error loading dynamically imported module|Importing a module script failed/i;

export function isChunkLoadError(error: any): boolean {
  return CHUNK_LOAD_ERROR_RE.test(error?.message ?? "");
}

function extractFailedUrl(
  error: any
): { url: string; kind: "js" | "css" } | undefined {
  const message = error?.message ?? "";
  const cssMatch = FAILED_CSS_URL_RE.exec(message);
  if (cssMatch) {
    return { url: cssMatch[1], kind: "css" };
  }
  const jsMatch = FAILED_MODULE_URL_RE.exec(message);
  if (jsMatch) {
    return { url: jsMatch[1], kind: "js" };
  }
  return undefined;
}

// Force a cache-bypassing re-fetch of the exact failed URL so a poisoned/
// stale HTTP cache entry for that chunk (e.g. Cloudflare Pages serving a
// stale response while a deploy propagates across its edge) doesn't linger.
function bustCache(url: string): Promise<unknown> {
  return fetch(url, { cache: "reload" }).catch(() => {});
}

// Vite's preload helper remembers every CSS/JS dependency URL it has ever
// attempted - successful or not - and silently skips re-attempting a
// failed one on a later call (it's a plain in-memory `{}` keyed by
// resolved URL, never cleared). So just re-invoking the original factory
// again would never actually reload a broken stylesheet - it'd just keep
// skipping it forever and rendering unstyled. Load it ourselves via a
// fresh <link> tag instead; the retry's factory() call will then harmlessly
// skip the CSS dependency it already knows about and go straight to the JS
// chunk, which by then has a real stylesheet in the document.
function reloadStylesheet(url: string): Promise<unknown> {
  return new Promise((resolve) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    // Best-effort - a still-broken stylesheet shouldn't block the rest of
    // the retry chain, missing styles are a much smaller problem than a
    // crash screen.
    link.addEventListener("load", resolve);
    link.addEventListener("error", resolve);
    document.head.appendChild(link);
  });
}

// Real browsers cache a dynamic import()'s *specifier* as permanently
// failed in their module map the first time it rejects - re-calling the
// exact same `import("./Foo")` after a failure replays that cached
// rejection from memory with no new network request at all (confirmed
// against real Chrome: only the manual bustCache() fetch below hit the
// network on a real retry attempt, the re-import itself never did). So a
// retry can't just re-invoke the original factory - it needs a URL the
// module map has never seen before, which we get by appending a
// cache-busting query string to the failed chunk's own URL and importing
// that directly. `importUrl` is injectable so tests can stub it without
// needing a real dynamic import of an arbitrary URL.
function importUrl<T extends React.ComponentType<any>>(
  url: string
): Promise<{ default: T }> {
  return import(/* @vite-ignore */ url);
}

export function retryImport<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  attemptsLeft = RETRY_ATTEMPTS,
  targetUrl?: string,
  loadUrl: (url: string) => Promise<{ default: T }> = importUrl,
  reloadCss: (url: string) => Promise<unknown> = reloadStylesheet
): Promise<{ default: T }> {
  const attempt = targetUrl ? () => loadUrl(targetUrl) : factory;
  return attempt().catch((error) => {
    if (attemptsLeft <= 1) {
      // Out of retries - let the error propagate so the nearest
      // ErrorCatcher shows its crash screen and reports to Slack, same as
      // before.
      throw error;
    }
    // A JS retry (via targetUrl) carries its own kind forward; otherwise
    // classify fresh from whatever this attempt just threw.
    const failed = targetUrl
      ? ({ url: targetUrl, kind: "js" } as const)
      : extractFailedUrl(error);
    const recover =
      failed?.kind === "css"
        ? bustCache(failed.url).then(() => reloadCss(failed.url))
        : failed
          ? bustCache(failed.url)
          : Promise.resolve();
    return recover.then(
      () =>
        new Promise<{ default: T }>((resolve, reject) => {
          setTimeout(() => {
            // Only JS failures carry a target URL into the next attempt -
            // a CSS failure has already been dealt with above, so the next
            // attempt just re-runs the original factory (which will pick
            // up the now-loaded stylesheet and proceed to the JS chunk).
            // Strip any query string the URL already carries (from a
            // previous retry) before appending a fresh one, so the module
            // map sees a new specifier on every attempt instead of the
            // same busted URL again.
            const nextTargetUrl =
              failed?.kind === "js"
                ? `${failed.url.split("?")[0]}?retry=${attemptsLeft}`
                : undefined;
            retryImport(
              factory,
              attemptsLeft - 1,
              nextTargetUrl,
              loadUrl,
              reloadCss
            ).then(resolve, reject);
          }, RETRY_DELAY_MS);
        })
    );
  });
}

export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() => retryImport(factory));
}
